import React from 'react'
import { Box, Grid, makeStyles, Typography } from '@material-ui/core'
import {
  Cancel as CancelIcon,
  CalendarToday as CalendarTodayIcon,
  CardMembership as CardMembershipIcon,
  EventAvailable as EventAvailableIcon,
  EventBusy as EventBusyIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Info as InfoIcon,
  LocalOffer as LocalOfferIcon,
  MonetizationOn as MonetizationOnIcon,
  SentimentVerySatisfied as SentimentVerySatisfiedIcon,
  Schedule as ScheduleIcon,
  Stars as StarsIcon,
  Warning as WarningIcon,
} from '@material-ui/icons'
import { useTranslation } from 'react-i18next'
import { useCancelSubscription, useSubscription } from '@/hooks'
import { GradientButton, LoadingState } from '@/components'
import Alert from '@material-ui/lab/Alert'
import { CancelSubscription } from './CancelSubscription'
import { navigate } from '@reach/router'

const useStyles = makeStyles((theme) => ({
  cancelButtonContainer: {
    marginTop: '1rem',
  },
  container: {
    height: '100%',
    padding: theme.spacing(1),
    borderShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
    borderRadius: '1rem',
    border: '1px solid #1e286425',
  },

  description: {
    color: '#1E2864',
    margin: '1rem 0',
    fontSize: '0.8rem',
    textAlign: 'justify',
  },

  errorContainer: {
    backgroundColor: '#ffebee',
    borderRadius: '0.5rem',
    border: '1px solid #1e286425',
  },

  errrorText: {
    color: '#d32f2f',
    fontSize: '0.8rem',
    textAlign: 'center',
    marginBottom: '1rem',
  },

  header: {
    color: '#6473AF',
    fontSize: '0.8rem',
    fontWeight: 500,
    marginLeft: '1rem',
    marginBottom: '0.5rem',
    textTransform: 'uppercase',
  },

  icon: {
    color: '#d65a79d7',
  },

  iconColumn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  label: {
    color: '#6473AF',
    textTransform: 'uppercase',
    fontSize: '0.7rem',
    fontWeight: 400,
  },

  root: {
    display: 'flex',
    flexDirection: 'column',
    // justifyContent: 'center',
    alignItems: 'center',
    justifyItems: 'center',
  },

  value: {
    fontWeight: 'bold',
    fontSize: '0.7rem',
  },
}))

interface DetailBoxProps {
  icon: React.ReactNode // Allows any MUI Icon or JSX element
  header: string
  value?: string
}

const DetailBox = ({ icon, header, value }: DetailBoxProps) => {
  const classes = useStyles()

  return (
    <Grid container className={classes.container} spacing={1}>
      {/* Left Column: Icon */}
      <Grid item xs={3} className={classes.iconColumn}>
        {icon}
      </Grid>

      {/* Right Column: Nested Grid for Rows */}
      <Grid item xs={9}>
        <Grid container direction="column">
          <Grid item>
            <Typography className={classes.label}>{header}</Typography>
          </Grid>
          <Grid item>
            <Typography className={classes.value}>{value || '-'}</Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

const formatPrice = (amount: number, currency?: 'HUF' | 'EUR') => {
  const numberFormat = currency === 'HUF' ? 'hu-HU' : 'de-DE'
  const currencyCode = currency || 'HUF'

  return new Intl.NumberFormat(numberFormat, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export function SubscriptionCard() {
  const { t } = useTranslation('subscriptionCard')
  const classes = useStyles()
  const { data: subscription, isLoading, isError, error } = useSubscription()
  const {
    mutate: cancelSubscription,
    isPending: isCanceling,
    isError: isCancelError,
    error: cancelError,
  } = useCancelSubscription()
  const [isModalOpen, setIsModalOpen] = React.useState(false)

  if (!subscription) {
    return null
  }

  const {
    planName,
    planCode,
    formattedNextBilling,
    formattedTermination,
    formattedStartedAt,
    terminationDate,
    amountDue,
    price,
    status,
    billingCycle,
    currency,
    personalVoucherCode,
    canCancel,
  } = subscription

  const formattedBillingCycle =
    billingCycle === 'Upfront' ? t('upfront') : t('recurring') || '-'

  const handleCancelSubscription = () => {
    if (!canCancel) {
      console.warn('Subscription cannot be cancelled')
      return
    }

    cancelSubscription(undefined, {
      onSuccess: () => {
        setIsModalOpen(false)
        console.log('Subscription cancelled')
        navigate('/account')
      },
    })
  }

  if (isLoading) {
    return <LoadingState label={t('loadingSubscription')} />
  }

  if (isCanceling) {
    return <LoadingState label={t('cancelingSubscription')} />
  }

  if (isError) {
    return (
      <>
        <Alert severity="error" className={classes.errorContainer}>
          <Typography variant="body1" className={classes.errrorText}>
            {t('errorLoadingSubscription')}
          </Typography>
          <Typography variant="body1" className={classes.errrorText}>
            {(error as Error).message}
          </Typography>
        </Alert>
      </>
    )
  }

  if (isCancelError) {
    return (
      <>
        <Alert severity="error" className={classes.errorContainer}>
          <Typography variant="body1" className={classes.errrorText}>
            {t('errorCancelingSubscription')}
          </Typography>
          <Typography variant="body1" className={classes.errrorText}>
            {(cancelError as Error).message}
          </Typography>
        </Alert>
      </>
    )
  }

  const statusIcon = () => {
    switch (status) {
      case 'Active':
        return <SentimentVerySatisfiedIcon className={classes.icon} />
      case 'Canceled':
        return <CancelIcon className={classes.icon} />
      case 'PastDue':
        return <WarningIcon className={classes.icon} />
      case 'Trialing':
        return <HourglassEmptyIcon className={classes.icon} />
      default:
        return <InfoIcon className={classes.icon} />
    }
  }

  const formatPlanName = (planCode?: string, planName?: string) => {
    if (planCode === 'payg') {
      return t(`planName.${planCode}`)
    }

    return planName
  }

  return (
    <>
      <Grid item xs={12} className={classes.root}>
        <Box>
          <Typography className={classes.header}>
            {t('subscriptionHeader')}
          </Typography>
          <Typography className={classes.description}>
            {t('subscriptionDescription')}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <DetailBox
              icon={<CardMembershipIcon className={classes.icon} />}
              header={t('planName')}
              value={formatPlanName(planCode, planName)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DetailBox
              icon={statusIcon()}
              header={t('status')}
              value={t(`status.${status}`) || '-'}
            />
          </Grid>

          {formattedNextBilling && (
            <Grid item xs={12} md={6}>
              <DetailBox
                icon={<CalendarTodayIcon className={classes.icon} />}
                header={t('nextBillingDate')}
                value={formattedNextBilling}
              />
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <DetailBox
              icon={<LocalOfferIcon className={classes.icon} />}
              header={t('personalVoucherCode')}
              value={personalVoucherCode || t('noPersonalVoucherCode') || '-'}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DetailBox
              icon={<EventAvailableIcon className={classes.icon} />}
              header={t('memberSince')}
              value={formattedStartedAt}
            />
          </Grid>

          {terminationDate && (
            <Grid item xs={12} md={6}>
              <DetailBox
                icon={<EventBusyIcon className={classes.icon} />}
                header={t('terminationDate')}
                value={formattedTermination}
              />
            </Grid>
          )}

          {billingCycle && (
            <Grid item xs={12} md={6}>
              <DetailBox
                icon={
                  billingCycle == 'Recurring' ? (
                    <ScheduleIcon className={classes.icon} />
                  ) : (
                    <StarsIcon className={classes.icon} />
                  )
                }
                header={t('billingCycle')}
                value={formattedBillingCycle}
              />
            </Grid>
          )}

          {price && (
            <Grid item xs={12} md={6}>
              <DetailBox
                icon={<MonetizationOnIcon className={classes.icon} />}
                header={t('price')}
                value={formatPrice(amountDue || price, currency)}
              />
            </Grid>
          )}
        </Grid>

        {canCancel && (
          <Grid
            container
            justifyContent="center"
            className={classes.cancelButtonContainer}
          >
            <GradientButton onClick={() => setIsModalOpen(true)}>
              {t('cancelSubscriptionButton')}
            </GradientButton>
          </Grid>
        )}
      </Grid>

      {canCancel && (
        <CancelSubscription
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          confirmText={t('confirmCancelText')}
          onConfirm={handleCancelSubscription}
        />
      )}
    </>
  )
}
